import { computed, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import type { DevicesFilters } from "@/_store/filters.module";
import type { DeviceOsOrClientVersions, DeviceStatusFilterItem } from "@/_store/devices.module";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import { AgentHealthIssue, DeviceProtectionStatus, DeviceVulnerability } from "@/constants/devices";
import { getVersionsList } from "@/_helpers/devices";

const agentHealthValues = Object.values(AgentHealthIssue) as string[];

type VersionItem = { subheader?: true; name: string };

interface UseDevicesFiltersOptions<T extends DevicesFilters> {
  modelValue: Ref<T>;
  emitUpdate: (value: T) => void;
  labels: Ref<DeviceLabel[]>;
  statuses: Ref<DeviceStatusFilterItem[]>;
  rawOsVersions: Ref<DeviceOsOrClientVersions | null>;
  rawClientVersions: Ref<DeviceOsOrClientVersions | null>;
}

/**
 * Shared filter logic for the devices filter components.
 *
 * Both the horizontal page filter (`DevicesFilters.vue`) and the vertical export
 * filter (`ExportDevicesFilters.vue`) consume this composable — they differ only
 * in layout/template, and in whether they edit the model directly (export) or a
 * `draft` copy (page filter). It exposes the writable model computeds, the item
 * lists, and the OS→client version helpers (`osVersionItems` / `clientVersionItems`
 * as functions of the selected OS version, plus the `isClientVersionUnavailable`
 * staleness check), so both editing modes share the same logic.
 */
export function useDevicesFilters<T extends DevicesFilters>(options: UseDevicesFiltersOptions<T>) {
  const { t } = useI18n();

  const deviceVulnerabilityItems = Object.values(DeviceVulnerability);
  const deviceProtectionStatusItems = Object.values(DeviceProtectionStatus);

  const getFilterItemDisplayName = (item: DeviceStatusFilterItem) => {
    if (!item.statusLabel) return item.name;
    return t(`devices.filters.deviceStatuses.${item.name}`);
  };

  const updateFilters = (patch: Partial<T>) => {
    options.emitUpdate({
      ...options.modelValue.value,
      ...patch,
    });
  };

  const associatedModel = computed({
    get: () => options.modelValue.value?.associated ?? null,
    set: (value: boolean | null) => {
      updateFilters({ associated: value } as Partial<T>);
    },
  });

  // Device statuses, agent health issues and labels are three independent arrays on the filter.
  const deviceStatusesModel = computed({
    get: () => options.modelValue.value?.statuses ?? [],
    set: (value: string[]) => {
      updateFilters({ statuses: value } as Partial<T>);
    },
  });

  const agentHealthModel = computed({
    get: () => options.modelValue.value?.agentHealth ?? [],
    set: (value: AgentHealthIssue[]) => {
      updateFilters({ agentHealth: value } as Partial<T>);
    },
  });

  const labelsModel = computed({
    get: () => options.modelValue.value?.labels ?? [],
    set: (value: string[]) => {
      updateFilters({ labels: value } as Partial<T>);
    },
  });

  const osVersionModel = computed({
    get: () => options.modelValue.value?.osVersion ?? null,
    set: (value: string | null) => {
      updateFilters({ osVersion: value } as Partial<T>);
    },
  });

  const clientVersionModel = computed({
    get: () => options.modelValue.value?.clientVersion ?? null,
    set: (value: string | null) => {
      updateFilters({ clientVersion: value } as Partial<T>);
    },
  });

  const vulnerabilitiesModel = computed({
    get: () => options.modelValue.value?.vulnerabilities ?? [],
    set: (value: DeviceVulnerability[]) => {
      updateFilters({ vulnerabilities: value } as Partial<T>);
    },
  });

  const protectionStatusesModel = computed({
    get: () => options.modelValue.value?.protectionStatuses ?? [],
    set: (value: DeviceProtectionStatus[]) => {
      updateFilters({ protectionStatuses: value } as Partial<T>);
    },
  });

  const searchModel = computed({
    get: () => options.modelValue.value?.search ?? null,
    set: (value: string | null) => {
      updateFilters({ search: value } as Partial<T>);
    },
  });

  const associationFilterItems = computed(() => {
    const baseItems = [
      { text: t("devices.filters.associated.associated"), value: true },
      { text: t("devices.filters.associated.notAssociated"), value: false },
    ];

    if (associatedModel.value !== null) {
      return [{ text: t("devices.filters.associated.all"), value: null }, ...baseItems];
    }

    return baseItems;
  });

  // The status endpoint returns device statuses and agent-health issues in one list; split them so
  // each select only offers its own options.
  const deviceStatusItems = computed(() =>
    (options.statuses.value ?? []).filter((status) => !agentHealthValues.includes(status.id))
  );
  const agentHealthItems = computed(() =>
    (options.statuses.value ?? []).filter((status) => agentHealthValues.includes(status.id))
  );
  const labelOnlyItems = computed(() => options.labels.value ?? []);

  // Version lists are exposed as functions of the selected OS version rather than refs tied to
  // `modelValue`. This lets each consumer pass whatever it edits — the page filter passes its `draft`,
  // the export filter passes the model — and the list stays reactive to that source (and to the raw
  // store data, which is read here). The client-version list is filtered by the OS version, the OS
  // list is not (see `getVersionsList`).
  const osVersionItems = (): VersionItem[] =>
    getVersionsList<VersionItem>(
      null,
      options.rawOsVersions.value ?? {},
      options.rawClientVersions.value ?? {}
    ).osVersions;

  const clientVersionItems = (osVersion: string | null | undefined): VersionItem[] =>
    getVersionsList<VersionItem>(
      osVersion ?? null,
      options.rawOsVersions.value ?? {},
      options.rawClientVersions.value ?? {}
    ).clientVersions;

  return {
    searchModel,
    associatedModel,
    deviceStatusesModel,
    agentHealthModel,
    labelsModel,
    osVersionModel,
    clientVersionModel,
    vulnerabilitiesModel,
    protectionStatusesModel,
    associationFilterItems,
    deviceStatusItems,
    agentHealthItems,
    labelOnlyItems,
    osVersionItems,
    clientVersionItems,
    deviceVulnerabilityItems,
    deviceProtectionStatusItems,
    getFilterItemDisplayName,
  };
}
